import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import ResetPassword from './components/ResetPassword';
import { supabase } from './supabaseClient';

// Auto sign-out after this long with no mouse/keyboard/scroll/touch activity.
const IDLE_LIMIT_MS = 20 * 60 * 1000; // 20 minutes

function App() {
  const [user, setUser] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const idleTimer = useRef(null);

  const handleLogin = (userData) => {
    setUser(userData);
    localStorage.setItem('vera_user', JSON.stringify(userData));
  };

  const handleLogout = useCallback(() => {
    setUser(null);
    localStorage.removeItem('vera_user');
    delete axios.defaults.headers.common['Authorization'];
    supabase.auth.signOut();
  }, []);

  // Keep every axios request carrying the current Supabase session token, so
  // the backend's requireAuth middleware can verify who's calling. Without
  // this, every /api/* request would now come back 401 — the backend and
  // this header-sync change have to ship together.
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${session.access_token}`;
      } else {
        // No valid Supabase session — don't trust a stale localStorage login.
        localStorage.removeItem('vera_user');
      }
      setInitializing(false);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${session.access_token}`;
      } else {
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
        localStorage.removeItem('vera_user');
      }
    });

    return () => listener.subscription.unsubscribe();
  }, []);

  // Restore the signed-in profile from localStorage (set by handleLogin on
  // successful sign-in) once we know whether there's a real Supabase session.
  useEffect(() => {
    if (initializing) return;
    const stored = localStorage.getItem('vera_user');
    if (stored) {
      setUser(JSON.parse(stored));
    }
  }, [initializing]);

  // Idle auto-logout: any activity resets the clock; no activity for
  // IDLE_LIMIT_MS signs the user out.
  useEffect(() => {
    if (!user) return undefined;

    const resetTimer = () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => {
        handleLogout();
      }, IDLE_LIMIT_MS);
    };

    const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
    activityEvents.forEach(evt => window.addEventListener(evt, resetTimer));
    resetTimer();

    return () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      activityEvents.forEach(evt => window.removeEventListener(evt, resetTimer));
    };
  }, [user, handleLogout]);

  if (initializing) {
    // Brief blank frame while we check for an existing Supabase session, so
    // the dashboard's own data-fetching effects never fire before the auth
    // header is set.
    return null;
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={user ? <Navigate to="/dashboard" /> : <Login onLogin={handleLogin} />}
      />
      <Route
        path="/dashboard"
        element={user ? <Dashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
      />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="*" element={<Navigate to={user ? "/dashboard" : "/login"} />} />
    </Routes>
  );
}

export default App;
